/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import MLPAUMBCovTermComponent from '../MLPAUMBCovTermComponent/MLPAUMBCovTermComponent';
import Coverage from '../Models/Coverage';
import '@xengage/gw-platform-translations';
import metadata from './MLPAUMBCoveragesComponent.metadata.json5';
import styles from './MLPAUMBCoveragesComponent.module.scss';
import './MLPAUMBCoveragesComponent.messages';


function MLPAUMBCoveragesComponent(props) {
    const {
        coverages,
        vehicle,
        showMonthlyPremium,
        optionalHasCheck,
        isUmb,
        loading,
        onCovChange,
        editMode,
        onGeneralChange,
        displayedLineCoverages,
        displayHelpMessage,
        displayHelpMessageLimit
    } = props;

    const [model, setModel] = useState([]);
    const isQnB = appConfig.portalCode === 'QB';
    const [showMonthlyPremiumState, setShowMonthlyPremium] = useState(showMonthlyPremium);

    // Wrapper function as coverage object getter needs a function
    const isLoading = () => {
        return loading;
    };

    useEffect(() => {
        let updatedModel = [];
        if (coverages && !isUmb) {
            if (vehicle) {
                if ('value' in coverages.lineCoverages) {
                    coverages.lineCoverages.value.forEach((cov) => {
                        cov.isLineCoverage = true;
                    });
                    updatedModel = coverages.lineCoverages.value
                        .concat(vehicle.coverages)
                        .concat(coverages.optionalCoverages_ML.value)
                        .map(_.partial(Coverage.create, _, isLoading, onCovChange));
                } else {
                    coverages.lineCoverages.forEach((cov) => {
                        cov.isLineCoverage = true; 
                    });
                    updatedModel = coverages.lineCoverages
                        .concat(vehicle.coverages)
                        .concat(coverages.optionalCoverages_ML)
                        .map(_.partial(Coverage.create, _, isLoading, onCovChange));
                }
            } else {
                updatedModel = coverages.map(_.partial(Coverage.create, _, isLoading, onCovChange));
            }
        }

        if (coverages && isUmb) {
            updatedModel = coverages.map(_.partial(Coverage.create, _, isLoading, onCovChange));
        }
        updatedModel = updatedModel.filter((cov) => cov !== undefined);
        setShowMonthlyPremium(updatedModel.some((cov) => cov.monthlyAmount_ML));
        setModel(updatedModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverages]);

    const covAmountDisplayed = useCallback((coverage, veh) => {
        if (displayedLineCoverages.has(coverage.code_ML)) {
            const vehId = displayedLineCoverages.get(coverage.code_ML);
            return vehId !== veh.publicID;
        }
        return false;
    }, [displayedLineCoverages]);

    const isYesNoOption = useCallback((term) => {
        let isYesNoOptionVal;
        if (term.hasOptions && term.options.length === 2) {
            const yesOptionAvailable = !_.isUndefined(term.options.find((opt) => opt.code === 'true'));
            const noOptionAvailable = !_.isUndefined(term.options.find((opt) => opt.code === 'false'));
            isYesNoOptionVal = yesOptionAvailable && noOptionAvailable;
        } else {
            isYesNoOptionVal = false;
        }
        return isYesNoOptionVal;
    }, []);

    const hasMultipleOptions = useCallback((term) => {
        return term.hasOptions && term.options.length > 1;
    }, []);

    const hasNoOptions = useCallback((term) => {
        return !term.hasOptions || term.options.length === 0;
    }, []);

    const getSingleOption = useCallback((term) => {
        let termValue;
        if (term.hasOptions) {
            const chosenTerm = term.options.find((opt) => opt.code === term.value);
            termValue = (chosenTerm || term.options[0]).name;
        } else {
            termValue = term.newValue;
        }
        return termValue;
    }, []);

    const getChosenTermValue = useCallback((term) => {
        let termValue;
        if (term?.hasOptions) {
            const chosenTerm = term.options.find((opt) => opt.code === term.value);
            termValue = (chosenTerm || term.options[0]).name;
        } else {
            termValue = term?.newValue;
        }
        return termValue;
    }, []);

    const getOptionName = useCallback((optionsTerm) => {
        if (!_.isUndefined(optionsTerm.options)) {
            const option = optionsTerm.options.find((element) => element.code === optionsTerm.value);
            return option ? option.name : '';
        }
        return undefined;
    }, []);

    const hasSingleOption = useCallback((term) => {
        return term.hasOptions && term.options.length === 1;
    }, []);

    const isTrailer = useCallback((veh) => {
        return veh.vehicleCategoryCode_Ext === 'trailer';
    }, []);

    const hasSuspendedLiability = useCallback((veh) => {
        // look at selected attribute for Suspended Liability coverage using vehicle public id to look up
        const suspendLiabCov = veh.coverages.find((cov) => cov.code_ML === 'PASuspendLiabilityCov_ML');
        return !!suspendLiabCov && suspendLiabCov.selected;
    }, []);

    const getPolicyLevelCostForCoverage = useCallback((coverage, veh) => {
        if (!veh) {
            return undefined;
        }

        if (isTrailer(vehicle) || hasSuspendedLiability(veh)) {
            return undefined;
        }
        if (!covAmountDisplayed(coverage, veh)) {
            displayedLineCoverages.set(coverage.code_ML, veh.publicID);
            return coverage.lineCovCost_Ext.amount;
        }
        return 'Included';
    }, [covAmountDisplayed, displayedLineCoverages, hasSuspendedLiability, isTrailer, vehicle]);

    const coverageSelectedChanged = useCallback((isSelected, coverage) => {
        coverage.selected = isSelected;
    }, []);

    const resolvers = {
        resolveComponentMap: {
            mlpaumbcovtermcomponent: MLPAUMBCovTermComponent
        },
        resolveClassNameMap: styles
    };
    
    const getMonthlyCovAmount = (cov)  => {
        if (!isUmb && cov.isLineCoverage) {
            if (cov.vehicleCovCosts_ML.length > 0) {
                const vehCov = cov.vehicleCovCosts_ML.find((vehCov) => vehCov.vehiclePublicID === vehicle.publicID);
                       
                return vehCov && vehCov.monthlyAmount_ML ? vehCov.monthlyAmount_ML.amount : undefined;
            }
            const numberOfVehicles = _.get(coverages, 'vehicleCoverages.length', 1);
            return cov.monthlyAmount_ML ? cov.monthlyAmount_ML.amount / numberOfVehicles : undefined    
        }
        return cov.monthlyAmount_ML ? cov.monthlyAmount_ML.amount : undefined
    }

    const generateCoverageOverrides = useCallback(() => {
        const coverageOverrides = model.map((cov, covIndex) => {
            return {
                [`covParentDiv${covIndex}`]: {
                    visible: cov.selected || editMode,
                    className: isQnB ? "table-row three-columns radioButtonQnB" : "table-row three-columns"
                },
                [`coverageDescTooltip${covIndex}`]: {
                    visible: displayHelpMessage,
                    tooltipContent: displayHelpMessageLimit ? cov.description : coverages[covIndex].description
                },
                [`coverageDescTooltipEditMode${covIndex}`]: {
                    visible: displayHelpMessage,
                    tooltipContent: cov.description
                },
                [`nonEditModeDiv${covIndex}`]: {
                    visible: !editMode
                },
                [`editModeDiv${covIndex}`]: {
                    visible: editMode
                },
                [`fieldSetEditModeRequired${covIndex}`]: {
                    visible: cov.required
                },
                [`fieldSetEditModeNonRequired${covIndex}`]: {
                    visible: !cov.required
                },
                [`editModeCovName${covIndex}`]: {
                    tooltip: {
                        text: cov.description
                    },
                    value: cov.name
                },
                [`requiredCoverageIcon${covIndex}`]: {
                    visible: cov.required,
                    className: isQnB ? "icon-ml-status-check-complete icon-secondary" : "icon-ml-status-check-complete icon-green"
                },
                [`requiredCoverageName${covIndex}`]: {
                    content: cov.name
                },
                [`requiredCoverageTooltip${covIndex}`]: {
                    visible: !!cov.description,
                    tooltipContent: cov.description,
                    noHeader: true
                },
                [`selectedCheckBoxEditMode${covIndex}`]: {
                    label: cov.name,
                    tooltip: {
                        text: cov.description
                    },
                    onValueChange: coverageSelectedChanged,
                    path: cov,
                    value: cov.selected
                },
                [`umbMobileLabel${covIndex}`]: {
                    visible: cov.selected && isUmb
                },
                [`nonUmbMobileLabel${covIndex}`]: {
                    visible: cov.selected && !isUmb
                },
                [`covTermLoop${covIndex}`]: {
                    data: cov.terms
                },
                [`rightColCovAmount${covIndex}`]: {
                    visible: (!_.isUndefined(_.get(cov, 'amount.amount', undefined)) && showMonthlyPremium === false)
                        || ((_.get(cov, 'amount.amount', 0) > 0) && !cov.monthlyAmount_ML),
                    showFractions: true,
                    tag: 'div',
                    currency: cov.amount ? cov.amount.currency : undefined,
                    value: cov.amount ? cov.amount.amount : undefined
                },
                [`rightColCovMonthlyAmount${covIndex}`]: {
                    visible: !!showMonthlyPremium && !_.isUndefined(_.get(cov, 'monthlyAmount_ML.amount', undefined)) && !!vehicle && !cov.isLineCovCost_Ext,
                    showFractions: true,
                    tag: 'div',
                    currency: cov.monthlyAmount_ML ? cov.monthlyAmount_ML.currency : undefined,
                    value: getMonthlyCovAmount(cov)
                },
                [`rightColLineCovCost${covIndex}`]: {
                    visible: !!vehicle && cov.isLineCovCost_Ext,
                    content: getPolicyLevelCostForCoverage(cov, vehicle)
                },
                [`covTermComponent${covIndex}`]: {
                    coverage: cov,
                    index: covIndex,
                    editMode: editMode,
                    getChosenTermValue: getChosenTermValue,
                    hasSingleOption: hasSingleOption,
                    getSingleOption: getSingleOption,
                    isYesNoOption: isYesNoOption,
                    hasMultipleOptions: hasMultipleOptions,
                    getOptionName: getOptionName,
                    hasNoOptions: hasNoOptions,
                },
                [`rightColMobileLabelMonthlyPrem${covIndex}`]: {
                    visible: !!showMonthlyPremiumState && !!_.get(cov, 'monthlyAmount_ML.amount', undefined)
                },
                [`rightColMobileLabelPrem${covIndex}`]: {
                    visible:  (_.get(cov, 'amount.amount', 0) > 0 && !showMonthlyPremiumState)
                        || ((_.get(cov, 'amount.amount', 0) > 0) && !cov.monthlyAmount_ML)
                }
            };
        });

        return Object.assign({}, ...coverageOverrides);
    }, [model, displayHelpMessage, editMode, coverageSelectedChanged, isUmb, showMonthlyPremium, vehicle, getPolicyLevelCostForCoverage, getChosenTermValue, hasSingleOption, getSingleOption, isYesNoOption, hasMultipleOptions, getOptionName, hasNoOptions, showMonthlyPremiumState]);

    const overrides = {
        nonShowMonthlyPremiumHeader: {
            visible: !showMonthlyPremiumState
        },
        showMonthlyPremiumHeader: {
            visible: showMonthlyPremiumState
        },
        nonIsUmbHeader: {
            visible: !isUmb
        },
        isUmbHeader: {
            visible: isUmb
        },
        umbCovsDivIterable: {
            data: model
        },
        ...generateCoverageOverrides()
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLPAUMBCoveragesComponent.propTypes = {
    coverages: PropTypes.shape({}),
    vehicle: PropTypes.shape({}),
    showMonthlyPremium: PropTypes.bool,
    optionalHasCheck: PropTypes.bool,
    isUmb: PropTypes.bool,
    loading: PropTypes.bool,
    onCovChange: PropTypes.func,
    editMode: PropTypes.bool,
    onGeneralChange: PropTypes.func,
    displayedLineCoverages: PropTypes.shape({}),
    displayHelpMessage: PropTypes.bool,
    displayHelpMessageLimit: PropTypes.bool
};

MLPAUMBCoveragesComponent.defaultProps = {
    isUmb: false,
    editMode: false,
    displayHelpMessage: true,
    displayHelpMessageLimit: true
};

export default MLPAUMBCoveragesComponent;
